import React, { useState, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import DokkaPayTitleText from '@/pages/dokkaPay/pages/company/components/PaymentsWorkspace/components/DokkaPayTitleText';

import LockImage from '@/pages/dokkaPay/pages/company/components/PaymentsWorkspace/components/RequestAccessScreen/images/Lock.svg';
import usePaymentProvidersConfig from '@/pages/dokkaPay/pages/configurations/company/pages/paymentProviders/components/Providers/hooks/usePaymentProvidersConfig';
import { PaymentProviderStatuses } from '@/domain/paySettings/helpers';
import { Chip } from '@/pages/dokkaPay/pages/configurations/company/pages/paymentProviders/components/Providers/components/Provider/styled';
import { requestAccessToProviderAction } from '@/domain/paySettings/actions';

const RequestAccessScreen: React.FC = () => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const [requestSent, setRequestSent] = useState(false);

  const paymentProvidersConfig = usePaymentProvidersConfig();

  const connectedProviders = paymentProvidersConfig.filter(
    ({ status }) => status === PaymentProviderStatuses.connected,
  );

  const providerTitle = connectedProviders[0]?.title ?? '';

  const handleRequestAccess = useCallback(() => {
    new Promise((resolve, reject) => {
      dispatch(
        requestAccessToProviderAction({
          payload: { id: connectedProviders[0]?.id },
          resolve,
          reject,
        }),
      );
    })
      .then(() => {
        setRequestSent(true);
      })
      .finally();
  }, [dispatch, setRequestSent, connectedProviders]);

  return (
    <Stack alignItems="center" textAlign="center" maxWidth={500} spacing={3} justifyContent="center" height="100%">
      <img src={LockImage} alt="lock-image" />
      <DokkaPayTitleText />
      <Typography variant="h4">
        {formatMessage({
          id: 'company.paymentsWorkspace.requestAccessScreen.title',
          defaultMessage: 'Request access to',
        })}
      </Typography>
      {connectedProviders.map(({ title, id, icon }) => (
        <Stack
          key={id}
          display="flex"
          direction="row"
          spacing={1}
          alignItems="center"
          width="100%"
          p={2}
          sx={(theme) => ({
            border: 1,
            borderColor: theme.palette.grey[200],
            backgroundColor: theme.palette.common.white,
            borderRadius: 2,
          })}
        >
          <Stack
            justifyContent="center"
            alignItems="center"
            sx={(theme) => ({
              padding: theme.spacing(0.5, 1),
              border: 1,
              borderColor: theme.palette.grey[200],
              backgroundColor: theme.palette.common.white,
              borderRadius: 2,
              minHeight: 34,
            })}
          >
            <img width={40} height="auto" src={icon} alt={title} />
          </Stack>
          <Typography>{[title]}</Typography>
          <Chip
            color="success"
            label={formatMessage({ id: 'settings.connections.connected', defaultMessage: 'Connected' })}
            size="small"
          />
        </Stack>
      ))}
      <Typography variant="body1">
        {formatMessage(
          {
            id: 'company.paymentsWorkspace.requestAccessScreen.description',
            defaultMessage:
              'Your company has successfully connected to {providerTitle}, but you don\'t have access to the system yet. You can use DOKKA Pay once your request is approved.',
          },
          { providerTitle },
        )}
      </Typography>
      <Button variant="contained" color="primary" disabled={requestSent} onClick={handleRequestAccess}>
        {formatMessage({
          id: 'company.paymentsWorkspace.requestAccessScreen.button',
          defaultMessage: 'Request access',
        })}
      </Button>
    </Stack>
  );
};

export default RequestAccessScreen;
